import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { 
  MessageSquare, 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  XCircle, 
  User, 
  Calendar,
  Search,
  RotateCcw,
  Image as ImageIcon,
  Send,
  FileText,
  Zap,
  AlertTriangle,
  X,
  RefreshCw,
  ExternalLink,
  Building,
  MessageCircle,
  Reply,
  Edit3,
  ChevronRight,
  ChevronLeft,
  Flag,
  Activity,
  Headphones,
  Circle,
  Eye,
  Trash2
} from 'lucide-react'
import { cn } from '../../utils/cn'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'

const SupportManagement = () => {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const isInitialLoad = React.useRef(true)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: ''
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const isInitialMount = React.useRef(true)

  const statusConfig = {
    open: { 
      color: 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-200', 
      icon: AlertCircle, 
      label: 'Open',
      borderColor: 'border-accent-200 dark:border-accent-700'
    },
    in_progress: { 
      color: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-200', 
      icon: Clock, 
      label: 'In Progress',
      borderColor: 'border-warning-200 dark:border-warning-700'
    },
    resolved: { 
      color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-200', 
      icon: CheckCircle, 
      label: 'Resolved',
      borderColor: 'border-success-200 dark:border-success-700'
    },
    closed: { 
      color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800/30 dark:text-neutral-200', 
      icon: XCircle, 
      label: 'Closed',
      borderColor: 'border-neutral-200 dark:border-neutral-700'
    }
  }

  const priorityConfig = {
    low: { color: 'text-success-600 dark:text-success-400', label: 'Low', bgColor: 'bg-success-50 dark:bg-success-900/20' },
    normal: { color: 'text-accent-600 dark:text-accent-400', label: 'Normal', bgColor: 'bg-accent-50 dark:bg-accent-900/20' },
    high: { color: 'text-warning-600 dark:text-warning-400', label: 'High', bgColor: 'bg-warning-50 dark:bg-warning-900/20' },
    urgent: { color: 'text-error-600 dark:text-error-400', label: 'Urgent', bgColor: 'bg-error-50 dark:bg-error-900/20' }
  }

  useEffect(() => {
    if (isInitialMount.current) {
      // Initial load - fetch immediately
      isInitialMount.current = false
      fetchMessages()
      return
    }

    // Check if this is a filter change that should be debounced
    const isFilterChange = filters.search.length > 0

    if (isFilterChange) {
      // Debounce search input
      const timeoutId = setTimeout(() => {
        fetchMessages()
      }, 500)
      
      return () => clearTimeout(timeoutId)
    } else {
      // Immediate fetch for pagination/sorting/filter clearing
      fetchMessages()
    }
  }, [pagination.page, filters, sortBy, sortOrder])

  const fetchMessages = async () => {
    try {
      if (isInitialLoad.current) setLoading(true)
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      })

      const response = await apiService.get(`/support?${queryParams}`)
      const data = response.data
      setMessages(data.messages)
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        pages: data.pagination.pages
      }))
    } catch (error) {
      console.error('Error fetching support messages:', error)
    } finally {
      if (isInitialLoad.current) {
        setLoading(false)
        isInitialLoad.current = false
      }
    }
  }

  const updateMessageStatus = async (messageId, updates) => {
    try {
      await apiService.put(`/support/${messageId}`, updates)

      // Refresh messages list
      fetchMessages()
      
      // Update selected message if it was updated
      if (selectedMessage && selectedMessage.id === messageId) {
        const updatedMessage = { ...selectedMessage, ...updates }
        setSelectedMessage(updatedMessage)
      }
    } catch (error) {
      console.error('Error updating message:', error)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  const getMessageDetails = async (messageId) => {
    try {
      const response = await apiService.get(`/support/${messageId}`)
      setSelectedMessage(response.data)
    } catch (error) {
      console.error('Error fetching message details:', error)
    }
  }

  // Auto-open ticket modal when ?open=id is present (e.g. from notification links)
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) getMessageDetails(openId)
  }, [searchParams])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const getTimeAgo = (timestamp) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diff = now - time
    
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return time.toLocaleDateString()
  }

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <Zap className="w-4 h-4 text-error-600 dark:text-error-400" />
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-warning-600 dark:text-warning-400" />
      case 'normal':
        return <Flag className="w-4 h-4 text-accent-600 dark:text-accent-500" />
      case 'low':
        return <Circle className="w-4 h-4 text-success-600 dark:text-success-400" />
      default:
        return <Flag className="w-4 h-4 text-accent-600 dark:text-accent-500" />
    }
  }

  // Add loading animation pattern like other admin pages
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading support tickets..." />
      </div>
    )
  }

  // Animation variants for consistent admin page animations
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <motion.div 
      className="pt-4 space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div 
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
        variants={itemVariants}
      >
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Support Management
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Manage and respond to client support requests
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => fetchMessages()}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2 border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-[#2f2b41] hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        className="grid grid-cols-2 lg:grid-cols-4 gap-6"
        variants={itemVariants}
      >
        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Total Tickets</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {pagination.total || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Open Tickets</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {messages.filter(m => m.status === 'open').length}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-br from-warning-400 to-warning-500 rounded-xl flex items-center justify-center shadow-lg">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Resolved</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {messages.filter(m => m.status === 'resolved').length}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-br from-success-400 to-success-500 rounded-xl flex items-center justify-center shadow-lg">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Urgent</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {messages.filter(m => m.priority === 'urgent').length}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-br from-error-400 to-error-500 rounded-xl flex items-center justify-center shadow-lg">
                <Zap className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 h-9 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <div className="min-w-[140px]">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full py-2 px-3 h-9 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent text-sm"
              >
                <option value="">All Statuses</option>
                {Object.entries(statusConfig).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div className="min-w-[140px]">
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full py-2 px-3 h-9 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent text-sm"
              >
                <option value="">All Priorities</option>
                {Object.entries(priorityConfig).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <Button
              onClick={() => {
                setFilters({ status: '', priority: '', search: '' })
                setPagination(prev => ({ ...prev, page: 1 }))
              }}
              variant="outline"
              size="sm"
              className="h-9 px-3"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tickets List */}
      <motion.div variants={itemVariants}>
        {messages.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
              <Headphones className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">No support tickets</h3>
            <p className="text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
              {Object.values(filters).some(filter => filter) 
                ? "No tickets match your current filters."
                : "No support tickets found."
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => {
              const StatusIcon = statusConfig[message.status]?.icon || AlertCircle
              const isUnread = !message.is_read
              
              return (
                <div
                key={message.id}
                onClick={() => getMessageDetails(message.id)}
                className={cn(
                  "p-4 border border-neutral-200 dark:border-[#2f2b41] rounded-lg hover:border-accent-300 dark:hover:border-accent-600 cursor-pointer transition-all bg-white dark:bg-[#252232] hover:shadow-md",
                  isUnread && "border-accent-200 dark:border-accent-700 bg-accent-50/50 dark:bg-[#252232]"
                )}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    {/* Priority Indicator */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      priorityConfig[message.priority]?.bgColor
                    )}>
                      <div className={cn("flex items-center", priorityConfig[message.priority]?.color)}>
                        {getPriorityIcon(message.priority)}
                      </div>
                    </div>

                    {/* Ticket Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className={cn(
                        "text-base font-semibold truncate mb-1",
                        isUnread
                          ? "text-neutral-900 dark:text-neutral-100"
                          : "text-neutral-700 dark:text-neutral-300"
                      )}>
                        {message.subject}
                      </h4>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                        <span className="flex items-center">
                          <Building className="w-3 h-3 mr-1" />
                          {message.company_name}
                        </span>
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {message.user_name}
                        </span>
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {getTimeAgo(message.created_at)}
                        </span>
                        {message.screenshot_url && (
                          <span className="flex items-center">
                            <ImageIcon className="w-3 h-3 mr-1" />
                            Screenshot
                          </span>
                        )}
                      </div>

                      <p className={cn(
                        "text-sm line-clamp-1",
                        isUnread
                          ? "text-neutral-700 dark:text-neutral-300"
                          : "text-neutral-600 dark:text-neutral-400"
                      )}>
                        {message.message}
                      </p>
                    </div>
                  </div>

                  {/* Status, Priority and Actions */}
                  <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-3 sm:ml-4 sm:flex-shrink-0">
                    {/* Status and Priority badges */}
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                        statusConfig[message.status]?.color,
                        statusConfig[message.status]?.borderColor
                      )}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig[message.status]?.label}
                      </div>

                      <div className={cn(
                        "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                        priorityConfig[message.priority]?.bgColor,
                        priorityConfig[message.priority]?.color
                      )}>
                        {priorityConfig[message.priority]?.label}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1">
                      {message.status !== 'resolved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            updateMessageStatus(message.id, { status: 'resolved' })
                          }}
                          className="text-success-600 hover:text-success-700 hover:bg-success-100 dark:text-success-400 dark:hover:text-success-300 dark:hover:bg-success-900/30 transition-all duration-200"
                        >
                          <CheckCircle className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Resolve</span>
                        </Button>
                      )}

                      {message.status !== 'in_progress' && message.status !== 'resolved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            updateMessageStatus(message.id, { status: 'in_progress' })
                          }}
                          className="text-warning-600 hover:text-warning-700 hover:bg-warning-100 dark:text-warning-400 dark:hover:text-warning-300 dark:hover:bg-warning-900/30 transition-all duration-200"
                        >
                          <Clock className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Progress</span>
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => getMessageDetails(message.id)}
                        className="text-accent-600 hover:text-accent-700 hover:bg-accent-100 dark:text-accent-400 dark:hover:text-accent-300 dark:hover:bg-accent-900/30 transition-all duration-200"
                      >
                        <Eye className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              )
            })}
          </div>
        )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="px-6 py-4 border border-neutral-200 dark:border-[#2f2b41] rounded-lg bg-white dark:bg-[#252232]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} tickets
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                variant="outline"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                variant="outline"
                size="sm"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
      </motion.div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <MessageDetailModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onUpdate={updateMessageStatus}
          onDelete={async (id) => {
            try {
              await apiService.delete(`/support/${id}`)
              setSelectedMessage(null)
              fetchMessages()
            } catch (error) {
              console.error('Error deleting ticket:', error)
            }
          }}
        />
      )}
    </motion.div>
  )
}

// Message Detail Modal Component
const MessageDetailModal = ({ message, onClose, onUpdate, onDelete }) => {
  const [response, setResponse] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [replies, setReplies] = useState([])
  const [loadingReplies, setLoadingReplies] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    const fetchReplies = async () => {
      try {
        setLoadingReplies(true)
        const res = await apiService.get(`/support/${message.id}/replies`)
        setReplies(res.data || [])
      } catch (error) {
        console.error('Error fetching replies:', error)
      } finally {
        setLoadingReplies(false)
      }
    }
    fetchReplies()
  }, [message.id])

  const statusConfig = {
    open: { color: 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-200', icon: AlertCircle, label: 'Open', borderColor: 'border-accent-200 dark:border-accent-700' },
    in_progress: { color: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-200', icon: Clock, label: 'In Progress', borderColor: 'border-warning-200 dark:border-warning-700' },
    resolved: { color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-200', icon: CheckCircle, label: 'Resolved', borderColor: 'border-success-200 dark:border-success-700' },
    closed: { color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800/30 dark:text-neutral-200', icon: XCircle, label: 'Closed', borderColor: 'border-neutral-200 dark:border-neutral-700' }
  }

  const priorityConfig = {
    low: { color: 'text-success-600 dark:text-success-400', label: 'Low', bgColor: 'bg-success-50 dark:bg-success-900/20' },
    normal: { color: 'text-accent-600 dark:text-accent-400', label: 'Normal', bgColor: 'bg-accent-50 dark:bg-accent-900/20' },
    high: { color: 'text-warning-600 dark:text-warning-400', label: 'High', bgColor: 'bg-warning-50 dark:bg-warning-900/20' },
    urgent: { color: 'text-error-600 dark:text-error-400', label: 'Urgent', bgColor: 'bg-error-50 dark:bg-error-900/20' }
  }

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <Zap className="w-3.5 h-3.5 text-error-600 dark:text-error-400" />
      case 'high': return <AlertTriangle className="w-3.5 h-3.5 text-warning-600 dark:text-warning-400" />
      case 'low': return <Circle className="w-3.5 h-3.5 text-success-600 dark:text-success-400" />
      default: return <Flag className="w-3.5 h-3.5 text-accent-600 dark:text-accent-400" />
    }
  }

  const handleSendReply = async (resolve = false) => {
    if (!response.trim()) return
    setIsSubmitting(true)
    try {
      await apiService.post(`/support/${message.id}/replies`, { message: response.trim() })
      toast.success(resolve ? 'Reply sent & ticket resolved' : 'Reply sent')
      if (resolve) {
        await onUpdate(message.id, { status: 'resolved' })
        onClose()
      } else {
        const res = await apiService.get(`/support/${message.id}/replies`)
        setReplies(res.data || [])
        setResponse('')
      }
    } catch (error) {
      console.error('Error sending reply:', error)
      toast.error(error.response?.data?.error || 'Failed to send reply')
    } finally {
      setIsSubmitting(false)
    }
  }

  const StatusIcon = statusConfig[message.status]?.icon || AlertCircle

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end sm:items-center justify-center p-0 sm:p-4">

        {/* Backdrop */}
        <motion.div
          className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />

        {/* Panel */}
        <motion.div
          className="relative w-full max-w-3xl mx-auto bg-white dark:bg-[#1a1625] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-100 dark:border-[#2f2b41]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 leading-snug truncate">
                  {message.subject}
                </h3>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                  <span className="flex items-center gap-1">
                    <Building className="w-3.5 h-3.5" />
                    {message.company_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {message.user_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(message.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Status badge */}
                <span className={cn(
                  "hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
                  statusConfig[message.status]?.color,
                  statusConfig[message.status]?.borderColor
                )}>
                  <StatusIcon className="w-3 h-3" />
                  {statusConfig[message.status]?.label}
                </span>
                {/* Priority badge */}
                <span className={cn(
                  "hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                  priorityConfig[message.priority]?.bgColor,
                  priorityConfig[message.priority]?.color
                )}>
                  {getPriorityIcon(message.priority)}
                  {priorityConfig[message.priority]?.label}
                </span>
                <Button onClick={onClose} variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col lg:flex-row gap-0 lg:gap-6 p-6">

              {/* Left: conversation thread */}
              <div className="flex-1 min-w-0 space-y-4">
                {/* Original message */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" /> Message
                  </p>
                  <div className="bg-neutral-50 dark:bg-[#1f1c2c] rounded-xl p-4 border border-neutral-200 dark:border-[#2f2b41]">
                    <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                      {message.message}
                    </p>
                  </div>
                </div>

                {/* Screenshot */}
                {message.screenshot_url && (
                  <a
                    href={message.screenshot_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg text-sm text-accent-600 dark:text-accent-400 hover:bg-accent-100 dark:hover:bg-accent-900/30 transition-colors"
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span className="font-medium">View attachment</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}

                {/* Conversation thread */}
                {(message.response_message || replies.length > 0 || !loadingReplies) && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                      <MessageCircle className="w-3.5 h-3.5" /> Conversation
                    </p>
                    <div className="space-y-2">
                      {/* Legacy response_message for tickets answered before threaded replies */}
                      {message.response_message && replies.length === 0 && (
                        <div className="bg-success-50 dark:bg-success-900/20 rounded-xl p-4 border border-success-200 dark:border-success-800">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-xs font-medium text-success-600 dark:text-success-400">Support Team</span>
                            {message.responded_at && (
                              <span className="text-xs text-neutral-400">{new Date(message.responded_at).toLocaleString()}</span>
                            )}
                          </div>
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                            {message.response_message}
                          </p>
                        </div>
                      )}

                      {loadingReplies ? (
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 py-2">Loading replies…</p>
                      ) : replies.length === 0 && !message.response_message ? (
                        <p className="text-xs text-neutral-400 dark:text-neutral-500 py-2 italic">No replies yet.</p>
                      ) : (
                        replies.map(reply => (
                          <div key={reply.id} className={cn(
                            "rounded-xl p-4 border",
                            reply.role === 'admin'
                              ? "bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800"
                              : "bg-neutral-50 dark:bg-[#1f1c2c] border-neutral-200 dark:border-[#2f2b41]"
                          )}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={cn(
                                "text-xs font-medium",
                                reply.role === 'admin' ? "text-success-600 dark:text-success-400" : "text-neutral-600 dark:text-neutral-400"
                              )}>
                                {reply.role === 'admin' ? 'Support Team' : reply.user_name}
                              </span>
                              <span className="text-xs text-neutral-400">{new Date(reply.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                              {reply.message}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Reply form */}
                {message.status !== 'closed' && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                      <Edit3 className="w-3.5 h-3.5" /> Reply
                    </p>
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Type your response to the client..."
                      rows={4}
                      className="w-full px-3 py-2.5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl bg-white dark:bg-[#221f2f] text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
                    />
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => onUpdate(message.id, { status: 'in_progress' })}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-1.5"
                      >
                        <Clock className="w-3.5 h-3.5" />
                        In Progress
                      </Button>
                      <Button
                        onClick={() => handleSendReply(false)}
                        disabled={!response.trim() || isSubmitting}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1.5"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {isSubmitting ? 'Sending…' : 'Send Update'}
                      </Button>
                      <Button
                        onClick={() => handleSendReply(true)}
                        disabled={!response.trim() || isSubmitting}
                        size="sm"
                        className="flex items-center gap-1.5"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        {isSubmitting ? 'Sending…' : 'Send & Resolve'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: management panel */}
              <div className="w-full lg:w-48 flex-shrink-0 mt-4 lg:mt-0">
                <div className="lg:sticky lg:top-0 space-y-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Status</p>
                    <select
                      value={message.status}
                      onChange={(e) => onUpdate(message.id, { status: e.target.value })}
                      className="w-full py-2 px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-lg bg-white dark:bg-[#221f2f] text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                    >
                      {Object.entries(statusConfig).map(([value, config]) => (
                        <option key={value} value={value}>{config.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Priority</p>
                    <div className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium",
                      priorityConfig[message.priority]?.bgColor,
                      priorityConfig[message.priority]?.color
                    )}>
                      {getPriorityIcon(message.priority)}
                      {priorityConfig[message.priority]?.label}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Contact</p>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{message.user_name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 break-all">{message.user_email}</p>
                  </div>

                  <div className="pt-2 border-t border-neutral-100 dark:border-[#2f2b41]">
                    {confirmDelete ? (
                      <div className="space-y-2">
                        <p className="text-xs text-error-600 dark:text-error-400 font-medium">Delete this ticket?</p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => onDelete(message.id)}
                            variant="destructive"
                            size="sm"
                            className="flex-1 text-xs"
                          >
                            Yes, delete
                          </Button>
                          <Button
                            onClick={() => setConfirmDelete(false)}
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setConfirmDelete(true)}
                        variant="ghost"
                        size="sm"
                        className="w-full flex items-center gap-1.5 text-error-600 hover:text-error-700 hover:bg-error-50 dark:text-error-400 dark:hover:text-error-300 dark:hover:bg-error-900/20"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete ticket
                      </Button>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default SupportManagement